import React from "react";

import type { LaneRendererProps } from "../lane.types";
import { GLASS_SECONDARY_TEXT_CLASS, buildSegmentBars, buildWaveHeights, itemVisualDensity, overlapCounts, productTone, readNumberMeta, readStringMeta, visibleLaneItems } from "./rendering";

function stateChip(label: string, tone: "sky" | "amber" | "rose" | "slate") {
  const className =
    tone === "sky"
      ? "border-sky-700/16 bg-sky-50/92 text-sky-950"
      : tone === "amber"
        ? "border-amber-700/16 bg-amber-50/92 text-amber-900"
        : tone === "rose"
          ? "border-rose-700/16 bg-rose-50/92 text-rose-900"
          : "border-slate-700/16 bg-slate-50/92 text-slate-900";

  return <span className={["rounded-full border px-1.5 py-0.5 text-[8px] font-semibold uppercase tracking-[0.12em]", className].join(" ")}>{label}</span>;
}

function contractStateChip(contractState: string | null | undefined) {
  switch (contractState) {
    case "proxy_stale":
      return stateChip("proxy stale", "amber");
    case "proxy_failed":
      return stateChip("proxy failed", "rose");
    case "proxy_missing":
      return stateChip("no proxy", "rose");
    case "deprecated_proxy_fallback":
      return stateChip("legacy proxy", "amber");
    case "direct_media_only":
      return stateChip("direct source", "amber");
    case "source_degraded":
      return stateChip("degraded source", "amber");
    case "offline":
      return stateChip("offline", "rose");
    case "missing":
      return stateChip("missing", "rose");
    default:
      return null;
  }
}

function AudioGeneratingOverlay({ widthPx, status }: { widthPx: number; status: "queued" | "generating" }) {
  const markerCount = widthPx >= 360 ? 3 : widthPx >= 180 ? 2 : 1;
  const positions = markerCount === 3 ? [0.12, 0.5, 0.88] : markerCount === 2 ? [0.2, 0.8] : [0.5];
  const label = status === "queued" ? "waveform queued" : "waveform generating";
  const pulseBars = Math.max(6, Math.min(18, Math.round(widthPx / 18)));

  return (
    <div className="pointer-events-none absolute inset-0 z-[1] overflow-hidden rounded-[inherit]">
      <div className="absolute inset-0 bg-[linear-gradient(135deg,rgba(15,23,42,0.14),rgba(251,191,36,0.10),rgba(15,23,42,0.18))]" />
      <div className="absolute inset-y-[8px] left-[-18%] w-[34%] animate-pulse rounded-full bg-[linear-gradient(90deg,rgba(255,255,255,0),rgba(255,251,235,0.34),rgba(255,255,255,0))] opacity-70" />
      <div className="absolute inset-x-2 bottom-[7px] flex h-6 items-end gap-[2px] opacity-80">
        {Array.from({ length: pulseBars }, (_, index) => {
          const band = 0.2 + (((index % 6) + 1) / 8);
          return <div key={index} className="min-w-0 flex-1 rounded-full bg-amber-100/30" style={{ height: `${Math.round(band * 100)}%` }} />;
        })}
      </div>
      {positions.map((position, index) => (
        <div
          key={index}
          className="absolute top-[9px] -translate-x-1/2 rounded-full border border-amber-700/22 bg-amber-50/92 px-2 py-0.5 text-[8px] font-semibold uppercase tracking-[0.16em] text-amber-900 shadow-[0_4px_12px_rgba(245,158,11,0.14)]"
          style={{ left: `${position * 100}%` }}
        >
          {label}
        </div>
      ))}
    </div>
  );
}

export default function AudioPrimaryLaneRenderer({ lane, items, viewport, syncBadge }: LaneRendererProps): JSX.Element {
  const tone = productTone(lane.productType);
  const waveHeights = buildWaveHeights(16, 2);
  const visibleItems = visibleLaneItems(items, viewport);
  const overlaps = overlapCounts(visibleItems);
  const laneMetadata = ((lane as { metadata?: Record<string, unknown> }).metadata ?? {}) as Record<string, unknown>;

  return (
    <div className="relative h-full min-h-[48px]" data-lane-product="audio_primary" data-lane-id={lane.id}>
      <div className="absolute inset-x-0 top-1/2 h-px -translate-y-1/2 bg-sky-200/10" />
      {visibleItems.length > 0 ? (
        visibleItems.map(({ item, leftPx, widthPx }) => {
          const itemMetadata = ((item as { metadata?: Record<string, unknown> }).metadata ?? {}) as Record<string, unknown>;
          const gainDb = readNumberMeta(item, "gainDb");
          const memberKind = readStringMeta(item, "memberKind");
          const proxyStatus = readStringMeta(item, "proxyStatus");
          const preferredProxyKind = readStringMeta(item, "preferredProxyKind");
          const proxyContractState = readStringMeta(item, "proxyContractState");
          const offlineState = readStringMeta(item, "offlineState");
          const analysisState = readStringMeta(item, "analysisState");
          const growingFile = itemMetadata.growingFile === true;
          const usingDeprecatedProxyFallback = itemMetadata.usingDeprecatedProxyFallback === true;
          const metadataRichnessScore = readNumberMeta(item, "metadataRichnessScore");
          const decodePerformanceScore = readNumberMeta(item, "decodePerformanceScore");
          const channelLayout = readStringMeta(item, "channelLayout");
          const label = "label" in item ? item.label : item.id;
          const density = itemVisualDensity(widthPx);
          const overlapCount = overlaps.get(item.id) ?? 1;
          const segmentBars = buildSegmentBars(Math.max(4, Math.min(18, Math.round(widthPx / 14))), overlapCount);
          const isProxyGenerating = proxyStatus === "queued" || proxyStatus === "generating";

          return (
            <div
              key={item.id}
              data-visual-lane-item-id={item.id}
              className="absolute top-1/2 h-10 -translate-y-1/2 overflow-hidden rounded-xl border border-sky-500/24 bg-[linear-gradient(135deg,rgba(226,232,240,0.94),rgba(186,230,253,0.82)_58%,rgba(15,23,42,0.22))] shadow-[0_8px_20px_rgba(14,165,233,0.12)]"
              style={{ left: leftPx, width: widthPx }}
              title={label}
            >
              {isProxyGenerating ? <AudioGeneratingOverlay widthPx={widthPx} status={proxyStatus} /> : null}
              <div className="absolute inset-x-2 bottom-[7px] flex h-6 items-end gap-[2px] opacity-85">
                {segmentBars.map((heightRatio, index) => (
                  <div key={index} className="min-w-0 flex-1 rounded-full bg-sky-950/24" style={{ height: `${Math.round(heightRatio * 100)}%` }} />
                ))}
              </div>
              <div className={density === "micro" ? "absolute inset-x-2 top-1.5 flex justify-between" : "absolute inset-x-2 top-1.5 flex items-start justify-between gap-2"}>
                {density !== "micro" ? (
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className="rounded-full border border-sky-700/18 bg-sky-50/92 px-1.5 py-0.5 text-[8px] font-bold uppercase tracking-[0.14em] text-sky-950">Audio</span>
                      <div className="truncate text-[11px] font-semibold text-slate-950">{label}</div>
                    </div>
                    <div className="mt-0.5">
                      <div className="flex items-center gap-1.5 overflow-hidden">
                        <span className="truncate rounded-full border border-slate-800/14 bg-slate-950/8 px-1.5 py-0.5 text-[8px] font-semibold uppercase tracking-[0.14em] text-slate-900/82">
                          {memberKind ?? "audio span"}
                        </span>
                        {channelLayout ? stateChip(channelLayout, "slate") : null}
                        {proxyStatus === "ready" && proxyContractState !== "deprecated_proxy_fallback" ? stateChip((preferredProxyKind ?? "proxy").replace(/_/g, " "), "sky") : null}
                        {proxyStatus === "queued" || proxyStatus === "generating" ? stateChip(`proxy ${proxyStatus}`, "amber") : null}
                        {contractStateChip(proxyContractState)}
                        {offlineState && offlineState !== "online" ? stateChip(offlineState, "rose") : null}
                        {growingFile ? stateChip("growing", "amber") : null}
                        {analysisState && analysisState !== "ready" ? stateChip(`analysis ${analysisState}`, "amber") : null}
                        {metadataRichnessScore != null && metadataRichnessScore < 0.65 ? stateChip("thin meta", "slate") : null}
                        {decodePerformanceScore != null && decodePerformanceScore < 0.5 ? stateChip("decode watch", "amber") : null}
                      </div>
                    </div>
                  </div>
                ) : <div className="rounded-full border border-sky-700/18 bg-sky-50/92 px-1.5 py-0.5 text-[8px] font-bold uppercase tracking-[0.14em] text-sky-950">AUD</div>}
                <div className="flex items-center gap-1">
                  {gainDb != null && density !== "micro" ? (
                    <span className="rounded-full border border-sky-700/16 bg-sky-50/92 px-1.5 py-0.5 text-[9px] font-semibold text-sky-950">
                      {gainDb.toFixed(1)} dB
                    </span>
                  ) : null}
                  {overlapCount > 1 ? (
                    <span className="rounded-full border border-sky-700/16 bg-sky-50/92 px-1.5 py-0.5 text-[8px] font-semibold uppercase tracking-[0.12em] text-sky-950">
                      x{overlapCount}
                    </span>
                  ) : null}
                </div>
              </div>
            </div>
          );
        })
      ) : (
        <div className={["flex min-h-[52px] items-center justify-between gap-3 rounded-2xl border px-3 py-3", tone.emptyClassName].join(" ")}>
          <div>
            <div className="flex items-center gap-2">
              <div className="text-[11px] font-semibold text-slate-950">Audio lane is waiting for a resolved member.</div>
              {readStringMeta({ ...lane, metadata: lane.metadata } as never, "proxyStatus") === "ready" && readStringMeta({ ...lane, metadata: lane.metadata } as never, "proxyContractState") !== "deprecated_proxy_fallback" ? stateChip((readStringMeta({ ...lane, metadata: lane.metadata } as never, "preferredProxyKind") ?? "proxy").replace(/_/g, " "), "sky") : null}
              {contractStateChip(readStringMeta({ ...lane, metadata: lane.metadata } as never, "proxyContractState"))}
              {readStringMeta({ ...lane, metadata: lane.metadata } as never, "offlineState") && readStringMeta({ ...lane, metadata: lane.metadata } as never, "offlineState") !== "online" ? stateChip(readStringMeta({ ...lane, metadata: lane.metadata } as never, "offlineState") ?? "offline", "rose") : null}
              {laneMetadata.growingFile === true ? stateChip("growing", "amber") : null}
              {(readNumberMeta({ ...lane, metadata: lane.metadata } as never, "metadataRichnessScore") ?? 1) < 0.65 ? stateChip("thin meta", "slate") : null}
            </div>
            <div className={["mt-1 text-[10px]", GLASS_SECONDARY_TEXT_CLASS].join(" ")}>
              {syncBadge?.reason ?? "When an audio projection becomes active, waveform spans and gain context will render here."}
            </div>
          </div>
          <div className="flex h-8 items-end gap-[3px] opacity-85">
            {waveHeights.map((height, index) => (
              <div key={index} className="w-1 rounded-full bg-sky-900/45" style={{ height }} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}